import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import GlassCard from "../components/GlassCard";
import PrimaryButton, { GlassButton } from "../components/PrimaryButton";
import BottomNav from "../components/BottomNav";
import PageShell from "../components/PageShell";
import { Send, Inbox } from "lucide-react";

const Feedback = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("received");
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeText, setComposeText] = useState("");
  const [composeRecipient, setComposeRecipient] = useState("");
  const [clients, setClients] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const isPractitioner = user?.role === "therapist" || user?.role === "coach";

  const load = async () => {
    const [r, s] = await Promise.all([
      api.get("/feedback?direction=received").then((x) => x.data || []),
      api.get("/feedback?direction=sent").then((x) => x.data || []),
    ]);
    setReceived(r);
    setSent(s);
  };

  useEffect(() => {
    load();
    if (isPractitioner) {
      api.get("/my-clients").then((r) => setClients(r.data || [])).catch(() => {});
    }
  }, [isPractitioner]);

  const items = tab === "received" ? received : sent;

  const send = async () => {
    setError("");
    if (!composeText.trim()) return;
    if (isPractitioner && !composeRecipient) {
      setError("Pick a client to send to.");
      return;
    }
    if (!isPractitioner && !user?.linked_practitioner_id) {
      setError("You need to connect to a practitioner first (Settings → Connect to Your Practitioner).");
      return;
    }
    setSending(true);
    try {
      await api.post("/feedback", {
        content: composeText.trim(),
        recipient_user_id: isPractitioner ? composeRecipient : null,
      });
      setComposeText("");
      setComposeRecipient("");
      setComposeOpen(false);
      setTab("sent");
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not send. Try again.");
    } finally {
      setSending(false);
    }
  };

  const composeDisabled = useMemo(() => {
    if (isPractitioner) return clients.length === 0;
    return !user?.linked_practitioner_id;
  }, [isPractitioner, clients, user]);

  return (
    <PageShell showSettings>
      <div className="pt-16 pb-32 max-w-2xl mx-auto flex flex-col gap-6 well-page-enter">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-[#2A3B32]">Feedback</h1>
          <p className="mt-2 text-sm text-[#4A5D52]">
            {isPractitioner
              ? "Send and receive feedback with your clients."
              : "Send and receive feedback with your practitioner."}
          </p>
        </div>

        {/* Tab toggle */}
        <div className="flex items-center justify-center">
          <div className="inline-flex bg-white/40 backdrop-blur-md border border-white/40 rounded-full p-1" data-testid="feedback-tabs">
            <button
              onClick={() => setTab("received")}
              data-testid="feedback-tab-received"
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                tab === "received" ? "bg-[#8BA38E] text-white shadow" : "text-[#4A5D52]"
              }`}
            >
              <Inbox size={14} className="inline mr-1" /> Received
            </button>
            <button
              onClick={() => setTab("sent")}
              data-testid="feedback-tab-sent"
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                tab === "sent" ? "bg-[#8BA38E] text-white shadow" : "text-[#4A5D52]"
              }`}
            >
              <Send size={14} className="inline mr-1" /> Sent
            </button>
          </div>
        </div>

        {/* Compose */}
        {!composeOpen ? (
          <PrimaryButton onClick={() => setComposeOpen(true)} disabled={composeDisabled} data-testid="feedback-compose-btn">
            <Send size={18} className="mr-2" />
            {isPractitioner ? "Send Feedback to a Client" : "Send Feedback to Practitioner"}
          </PrimaryButton>
        ) : (
          <GlassCard data-testid="feedback-compose-card">
            <h3 className="text-xs font-semibold text-[#8BA38E] uppercase tracking-wider">New Feedback</h3>
            {isPractitioner && (
              <>
                <label className="mt-3 block text-sm text-[#4A5D52]">To</label>
                <select
                  value={composeRecipient}
                  onChange={(e) => setComposeRecipient(e.target.value)}
                  data-testid="feedback-recipient-select"
                  className="mt-1 w-full h-[48px] bg-white/60 border border-white/40 text-[#2A3B32] rounded-[16px] px-3 focus:outline-none focus:ring-2 focus:ring-[#8BA38E]"
                >
                  <option value="">— Select a client —</option>
                  {clients.map((c) => (
                    <option key={c.user_id} value={c.user_id}>{c.name} ({c.email})</option>
                  ))}
                </select>
              </>
            )}
            <textarea
              value={composeText}
              onChange={(e) => setComposeText(e.target.value)}
              rows={6}
              placeholder={isPractitioner ? "Write feedback or notes for this client…" : "Share thoughts with your practitioner…"}
              data-testid="feedback-compose-textarea"
              className="mt-3 w-full bg-white/60 border border-white/40 text-[#2A3B32] rounded-[16px] p-3 focus:outline-none focus:ring-2 focus:ring-[#8BA38E] resize-none"
            />
            {error && <p className="mt-2 text-sm text-[#B85C5C]" data-testid="feedback-compose-error">{error}</p>}
            <div className="mt-4 flex flex-col gap-2">
              <PrimaryButton onClick={send} disabled={sending || !composeText.trim()} data-testid="feedback-send-btn">
                {sending ? "Sending…" : "Send"}
              </PrimaryButton>
              <GlassButton onClick={() => { setComposeOpen(false); setError(""); }} data-testid="feedback-cancel-btn">
                Cancel
              </GlassButton>
            </div>
          </GlassCard>
        )}

        {composeDisabled && (
          <p className="text-center text-xs text-[#8A9C91]" data-testid="feedback-disabled-hint">
            {isPractitioner
              ? "No connected clients yet. Share your join code from Settings."
              : "Connect to a practitioner in Settings to send feedback."}
          </p>
        )}

        {/* List */}
        <div className="flex flex-col gap-3">
          {items.length === 0 && (
            <GlassCard className="text-center" data-testid={`feedback-empty-${tab}`}>
              <p className="text-[#4A5D52]">
                {tab === "received" ? "No feedback received yet." : "You haven't sent any feedback yet."}
              </p>
            </GlassCard>
          )}
          {items.map((fb) => {
            const partyLabel = tab === "received" ? `From ${fb.sender_name || "Someone"}` : `To ${fb.recipient_name || "Someone"}`;
            return (
              <GlassCard key={fb.feedback_id} data-testid={`feedback-${fb.feedback_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-[#2A3B32]">{partyLabel}</p>
                  <p className="text-xs text-[#8A9C91]">{new Date(fb.created_at).toLocaleDateString()}</p>
                </div>
                <p className="mt-3 text-[#2A3B32] whitespace-pre-wrap leading-relaxed">{fb.content}</p>
              </GlassCard>
            );
          })}
        </div>
      </div>
      <BottomNav />
    </PageShell>
  );
};

export default Feedback;
